import React from 'react';
import OntologyGraph from './OntologyGraph';
import ComponentWrapper from '../../../../common/ComponentWrapper';
import BounceLoader from '../../../../common/BounceLoader';
import { getNodeColor } from '../../../../utils';
import { isEqual } from '../../../../utils/lodash';

const OntologyGraphWrapper = React.forwardRef((props, ref) => {
  const componentRef = React.useRef();
  
  React.useImperativeHandle(ref, () => ({
    updateGraph: () => {
      if (componentRef.current) {
        componentRef.current.updateGraph();
      }
    },
    getGraphData: () => {
      if (componentRef.current && componentRef.current.state) {
        const graphData = componentRef.current.state.graph;
        
        return graphData;
      }
      return { concepts: [], individuals: [] };
    },
    getLastUpdateTime: () => {
      if (componentRef.current && componentRef.current.state) {
        return componentRef.current.state.lastUpdateTime;
      }
      return null;
    },
    trackDeletedIds: (deletedIds) => {
      if (componentRef.current) {
        componentRef.current.trackDeletedIds(deletedIds);
      }
    }
  }));

  return <OntologyGraphWrapperClass ref={componentRef} {...props} />;
});

const OntologyGraphWrapperClass = class extends ComponentWrapper {
  state = {
    graph: {
      concepts: [],
      individuals: []
    },
    loaded: false,
    lastUpdateTime: null,
    resetCounter: 0
  };

  // Add debouncing to prevent excessive updates
  updateTimeout = null;
  isUpdating = false;
  lastGraphData = null;
  recentlyDeletedIds = new Set(); // Track recently deleted IDs

  componentDidMount() {
      this.updateGraph();
  };

  // Centralized graph data processing
  processGraphData = (rawGraphData) => {
    if (!rawGraphData) {
      return { concepts: [], individuals: [] };
    }

    // Deduplicate concepts by UID
    const uniqueConcepts = new Map();
    if (rawGraphData.concepts && rawGraphData.concepts.length > 0) {
      rawGraphData.concepts.forEach((concept, index) => {
        const uid = concept.uid || `concept_${index}`;
        // Skip if this concept was recently deleted
        if (this.recentlyDeletedIds.has(concept.id)) {
          return;
        }
        if (!uniqueConcepts.has(uid)) {
          uniqueConcepts.set(uid, {
            ...concept,
            color: getNodeColor(concept.id),
            rawId: concept.id
          });
        } else {
          console.warn('Duplicate concept found and removed:', uid);
        }
      });
    }
    // Build a map of concept uid to predicates
    const conceptPredicates = {};
    uniqueConcepts.forEach((concept, uid) => {
      conceptPredicates[uid] = concept.predicates || {};
    });
    // Deduplicate individuals by UID
    const uniqueIndividuals = new Map();
    if (rawGraphData.individuals && rawGraphData.individuals.length > 0) {
      rawGraphData.individuals.forEach((individ, index) => {
        // Skip if this individual was recently deleted
        if (this.recentlyDeletedIds.has(individ.id)) {
          console.log('OntologyGraphWrapper: Skipping recently deleted individual:', individ.id);
          return;
        }
        const uid = individ.uid || `individ_${index}`;
        if (!uniqueIndividuals.has(uid)) {
          // Add predicate type to each triplet
          const isaArr = Array.isArray(individ.isa) ? individ.isa : [];
          const newTriplets = {};
          Object.keys(individ.triplets || {}).forEach((pred) => {
            let type = undefined;
            for (let i = 0; i < isaArr.length; i++) {
              const conceptUid = isaArr[i];
              if (conceptPredicates[conceptUid] && conceptPredicates[conceptUid][pred]) {
                type = conceptPredicates[conceptUid][pred].type;
                break;
              }
            }
            newTriplets[pred] = {
              ...individ.triplets[pred],
              color: getNodeColor(individ.id),
              type: type
            };
          });
          uniqueIndividuals.set(uid, {
            ...individ,
            triplets: newTriplets,
            rawId: individ.id
          });
        } else {
          console.warn('Duplicate individual found and removed:', uid);
        }
      });
    }
    const processedGraph = {
      concepts: Array.from(uniqueConcepts.values()),
      individuals: Array.from(uniqueIndividuals.values())
    };
    return processedGraph;
  };

  // Method to track deleted IDs
  trackDeletedIds = (deletedIds) => {
    deletedIds.forEach(id => {
      this.recentlyDeletedIds.add(id);
    });
    // Clear the deleted IDs after a delay
    setTimeout(() => {
      deletedIds.forEach(id => {
        this.recentlyDeletedIds.delete(id);
      });
    }, 5000); // Keep for 5 seconds
  };

  updateGraph = () => {
    // Clear any pending update
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    // If already updating, schedule another update after current one completes
    if (this.isUpdating) {
      this.updateTimeout = setTimeout(() => {
        this.updateGraph();
      }, 1000);
      return;
    }
    this.isUpdating = true;
    console.log('OntologyGraphWrapper: Fetching graph data...');
    this.get({
        url: `/meta_query?meta_query=["graph"]`,
        cb: (res) => {
          if (res && res._meta_query && res._meta_query.graph) {
            const rawGraphData = res._meta_query.graph;
            console.log('OntologyGraphWrapper: Raw graph data:', {
              conceptsCount: rawGraphData.concepts ? rawGraphData.concepts.length : 0,
              individualsCount: rawGraphData.individuals ? rawGraphData.individuals.length : 0,
              conceptIds: rawGraphData.concepts ? rawGraphData.concepts.map(c => c.id) : [],
              individualIds: rawGraphData.individuals ? rawGraphData.individuals.map(i => i.id) : []
            });
            // Process and deduplicate the graph data
            const processedGraph = this.processGraphData(rawGraphData);
            console.log('OntologyGraphWrapper: Processed graph data:', {
              conceptsCount: processedGraph.concepts.length,
              individualsCount: processedGraph.individuals.length,
              conceptUids: processedGraph.concepts.map(c => c.uid),
              individualUids: processedGraph.individuals.map(i => i.uid)
            });
            // Only update if data actually changed (deep compare)
            if (!this.lastGraphData || !isEqual(processedGraph, JSON.parse(this.lastGraphData))) {
              this.lastGraphData = JSON.stringify(processedGraph);
              this.setState({ 
                  graph: processedGraph,
                  loaded: true,
                  lastUpdateTime: Date.now()
              });
              console.log('OntologyGraphWrapper: Graph state updated');
            } else {
              console.log('OntologyGraphWrapper: Graph data unchanged, skipping update');
            }
          } else {
            console.log('OntologyGraphWrapper: No graph data in response, keeping existing data');
            // Don't clear the existing graph data if the response is empty
            // This prevents the graph from disappearing when there are temporary API issues
          }
          this.isUpdating = false;
        },
        errorCb: (error) => {
          console.error('OntologyGraphWrapper: Graph update error:', error);
          this.isUpdating = false;
        },
        disableNotify: true
    });
  };

  componentWillUnmount() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
  };

  render() {
    const { graph, loaded, resetCounter } = this.state;
    const { graphSettings } = this.props;
    
    if (!loaded) {
      return (
        <div style={{ 
          display: 'flex', 
          height: '100vh', 
          alignItems: 'center', 
          justifyContent: 'center',
          marginTop: '-40px'
        }}>
          <BounceLoader isLoading={true} />
        </div>
      );
    }

    console.log('Rendering graph with data:', graph);
    return (
      <OntologyGraph 
        graph={graph} 
        updateGraph={this.updateGraph}
        trackDeletedIds={this.trackDeletedIds}
        graphSettings={graphSettings}
        resetCounter={resetCounter}
      />
    );
  }
};

OntologyGraphWrapper.displayName = 'OntologyGraphWrapper';

export default OntologyGraphWrapper;
